import { Box, Flex, Text } from '@chakra-ui/react';
import type { CSSProperties, ReactNode } from 'react';

// Keep the native radio focusable + announced by AT (unlike display:none) while
// hiding it visually behind the custom card.
const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  overflow: 'hidden',
  // `clip` is deprecated in CSS; `clip-path` is the visually-hidden idiom now.
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
};

export interface RadioOption<T extends string | number> {
  value: T;
  label: string;
  description?: string;
  trailing?: ReactNode;
}

export interface RadioCardsProps<T extends string | number> {
  value: T;
  onChange: (value: T) => void;
  options: RadioOption<T>[];
  name?: string;
}

/**
 * Figma-styled pill radio cards (the import-mode / option selector look).
 *
 * Sized from pdf-to-figma, which is the reference the family is matched to. This was `sm`
 * labels with `py="3"` — roughly double — so a row here stood about 45% taller than the same
 * row in pdf. Same drift as SectionLabel's `md`, and the same fix: take pdf's tokens rather
 * than approximate them. Verified by rendering pdf's settings screen before and after: 0 of
 * 1,104,000 pixels differ.
 */
export function RadioCards<T extends string | number>({
  value,
  onChange,
  options,
  name = 'radio-cards',
}: RadioCardsProps<T>) {
  return (
    <Box display="flex" flexDirection="column" gap="1.5">
      {options.map((option) => {
        const checked = value === option.value;
        return (
          <Box
            as="label"
            key={String(option.value)}
            display="flex"
            alignItems="center"
            gap="2"
            px="2.5"
            py="1.5"
            borderWidth="1px"
            borderColor={checked ? 'figma.accent' : 'figma.border'}
            bg={checked ? 'figma.accentMuted' : 'transparent'}
            borderRadius="md"
            cursor="pointer"
            transition="all 0.15s"
            _hover={{
              bg: checked ? 'figma.accentMuted' : 'figma.panelHover',
              borderColor: checked ? 'figma.accent' : 'figma.borderStrong',
            }}
          >
            <input
              type="radio"
              name={name}
              checked={checked}
              onChange={() => onChange(option.value)}
              style={VISUALLY_HIDDEN}
            />
            <Box
              w="12px"
              h="12px"
              minW="12px"
              borderRadius="full"
              borderWidth="1.5px"
              borderColor={checked ? 'figma.accent' : 'figma.border'}
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Box
                w="6px"
                h="6px"
                borderRadius="full"
                bg="figma.accent"
                transform={checked ? 'scale(1)' : 'scale(0)'}
                transition="transform 0.15s"
              />
            </Box>
            <Box flex="1" minW="0">
              <Text
                fontSize="2xs"
                color="figma.text"
                fontWeight="500"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {option.label}
              </Text>
              {option.description && (
                <Text fontSize="2xs" color="figma.textMuted" mt="0.5">
                  {option.description}
                </Text>
              )}
            </Box>
            {option.trailing && (
              <Flex flexShrink={0} align="center">
                {option.trailing}
              </Flex>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
